import React, { useState, useEffect } from 'react';
import {
    Form, Input, InputNumber, Button, Space, Upload, message, Divider, Modal, Tabs, Select, Switch, Alert, Row, Col,
} from 'antd';
import {
    PlusOutlined,
    SaveOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { ImageUploader } from '@/components/ui/ImageUploader';
import { resizeImage, resizeImageToFile } from '@/utils/imageUtils';
import { uploadManyImagesAndGetDownloadUrls, uploadSingleImageAndGetDownloadUrl } from '@/utils/ImagesUploadToS3';
import { RoomData } from '@/AppRedux/actions/admin/roomDetailConfig/roomDetailConfigType';
import { MAX_ROOM_IMAGES } from '@/constants/AppConfig';
import { UploadGalleryStyle } from '@/components/ui/GalleryContainerStyle';
import TiptapEditor from "@/components/ui/TiptapEditor";

const { TextArea } = Input;
const { TabPane } = Tabs;

type RoomFormProps = {
    mode: 'create' | 'edit';
    initialData?: RoomData;
    propCode: string;
    onSubmit: (values: RoomData) => Promise<void> | void;
    onCancel?: () => void;
    loading?: boolean;
}

type CoverImageState = {
    file: File | null;
    previewUrl?: string;
    existingUrl?: string;
};

const allSupportedLanguages: { [key: string]: string } = {
    en: "English",
    th: "Thai",
    zh: "Chinese",
    ru: "Russian",
    ko: "Korean",
    ja: "Japanese",
};

const RoomForm: React.FC<RoomFormProps> = ({
    mode,
    initialData,
    propCode,
    onSubmit,
    onCancel,
    loading = false,
}) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    // State for images
    const [coverImageState, setCoverImageState] = useState<CoverImageState>({ file: null, previewUrl: undefined, existingUrl: undefined });
    const [galleryImages, setGalleryImages] = useState<UploadFile[]>([]);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');

    // State for dynamic tabs and language management
    const [editableLangs, setEditableLangs] = useState<string[]>(['en']);
    const [activeLang, setActiveLang] = useState('en');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [langToAdd, setLangToAdd] = useState<string | null>(null);

    // State for enabled toggle
    const [enabled, setEnabled] = useState<boolean>(true);

    // Initialize form with data when in edit mode
    useEffect(() => {
        if (mode === 'edit' && initialData) {
            form.setFieldsValue({
                roomtype_code: initialData.room_code,
                max_avail: initialData.max_avail ?? null,
                avail_offset: initialData.avail_offset != null ? Math.abs(initialData.avail_offset) : null,
            });

            const initialContents: { [key: string]: any } = {};
            const initialEditableLangs = Array.from(new Set(['en', ...initialData.contents.map(c => c.lang)]));

            initialEditableLangs.forEach(lang => {
                const contentItem = initialData.contents.find(c => c.lang === lang);
                initialContents[lang] = {
                    display_name: contentItem?.display_name || '',
                    description: contentItem?.description || '',
                    highlights: contentItem?.highlights?.join(', ') || '',
                    amenities: contentItem?.amenities?.join(', ') || '',
                };
            });

            form.setFieldsValue({ contents: initialContents });
            setEditableLangs(initialEditableLangs);

            if (initialData.cover_image) {
                setCoverImageState(prev => ({ ...prev, existingUrl: initialData.cover_image as string }));
            }

            if (initialData.images && initialData.images.length > 0) {
                setGalleryImages(initialData.images.map((img, index) => ({ uid: `-${index + 2}`, name: `image-${index + 1}.jpg`, status: 'done', url: img as string })));
            }

            // Initialize enabled state from initialData
            if (typeof initialData.enabled === 'boolean') {
                setEnabled(initialData.enabled);
            }
        }
    }, [mode, initialData, form]);

    const handleCancel = () => setPreviewOpen(false);

    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await resizeImage(file.originFileObj as RcFile);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
        setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
    };

    const handleAddLanguage = () => {
        if (langToAdd) {
            setEditableLangs(prev => [...prev, langToAdd]);
            setActiveLang(langToAdd);
            setIsModalVisible(false);
            setLangToAdd(null);
        }
    };

    const onFinishFailed = (errorInfo: any) => {
        const firstError = errorInfo.errorFields?.[0];
        if (!firstError) return;

        const fieldPath = firstError.name;
        const fieldMessage = firstError.errors?.[0];
        const isContentField = fieldPath.includes('contents');

        if (isContentField) {
            const errorLang = fieldPath[1];
            message.error(`Please complete all required fields in the "${allSupportedLanguages[errorLang] || errorLang}" tab.`);
            setActiveLang(errorLang);
        } else {
            message.error(fieldMessage || 'Please complete all required fields.');
        }

        form.scrollToField(fieldPath, { behavior: 'smooth', block: 'center' });
    };

    const handleCopyFromEn = () => {
        const enContent = form.getFieldValue(['contents', 'en']) || {};
        const next = {
            display_name: enContent.display_name || '',
            description: enContent.description || '',
            highlights: enContent.highlights || '',
            amenities: enContent.amenities || '',
        };
        const currentContents = form.getFieldValue('contents') || {};
        form.setFieldsValue({
            contents: {
                ...currentContents,
                [activeLang]: next,
            },
        });
    };

    const handleFinish = async (values: any) => {
        if (!propCode) {
            message.error('propCode is missing. Cannot upload images.');
            return;
        }

        setSubmitting(true);

        try {

            let coverImageUrl = coverImageState.existingUrl || null;
            if (coverImageState.file) {
                message.loading({ content: 'Uploading cover image...', key: 'upload' });
                const resizedCover = await resizeImageToFile(coverImageState.file, 1920, 0.85);
                coverImageUrl = await uploadSingleImageAndGetDownloadUrl({
                    propCode,
                    file: resizedCover,
                });
            }

            const newGalleryFiles = galleryImages
                .map(f => f.originFileObj)
                .filter((f): f is RcFile => !!f);

            let newGalleryUrls: string[] = [];
            if (newGalleryFiles.length > 0) {
                message.loading({ content: `Uploading ${newGalleryFiles.length} gallery images...`, key: 'upload' });
                const resizedGalleryFiles = await Promise.all(newGalleryFiles.map(f => resizeImageToFile(f, 1200, 0.80)));
                newGalleryUrls = await uploadManyImagesAndGetDownloadUrls({
                    propCode,
                    files: resizedGalleryFiles,
                });
            }

            const existingGalleryUrls = galleryImages
                .map(f => f.url)
                .filter((url): url is string => !!url);

            const finalGalleryUrls = [...existingGalleryUrls, ...newGalleryUrls];

            const contentsPayload = editableLangs.map(lang => {
                const langContent = values.contents?.[lang] || {};
                const highlights = langContent.highlights?.split(',').map((h: string) => h.trim()).filter(Boolean) || [];
                const amenities = langContent.amenities?.split(',').map((a: string) => a.trim()).filter(Boolean) || [];

                return {
                    lang,
                    display_name: langContent.display_name || '',
                    description: langContent.description || '',
                    highlights,
                    amenities
                };
            });


            const formData: RoomData = {
                id: mode === 'edit' ? initialData?.id ?? null : null,
                room_code: values.roomtype_code,
                cover_image: coverImageUrl,
                images: finalGalleryUrls,
                contents: contentsPayload,
                enabled: enabled,
                max_avail: values.max_avail ?? null,
                avail_offset: values.avail_offset ?? null,
            };
            onSubmit(formData);

        } catch (error) {
            console.error('Failed to upload images or submit form:', error);
            const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
            message.error({ content: `Submission failed: ${errorMessage}`, key: 'upload', duration: 5 });
        } finally {
            setTimeout(() => {
                setSubmitting(false);
            }, 2000);

        }
    };


    const beforeUploadGallery = (file: RcFile, batch: RcFile[]) => {
        if (galleryImages.length + batch.length > MAX_ROOM_IMAGES) {
            if (file === batch[0]) {
                message.error(
                    `Cannot upload more than ${MAX_ROOM_IMAGES} images. Please remove some images first.`
                );
            }
            return Upload.LIST_IGNORE;
        }
        return false;
    };

    const availableLanguagesToAdd = Object.keys(allSupportedLanguages).filter(l => !editableLangs.includes(l));
    const uploadButton = (
        <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    );

    return (
        <>
            <Form form={form} layout="vertical" onFinish={handleFinish} onFinishFailed={onFinishFailed} autoComplete="off" scrollToFirstError={true}>
                <Divider>Basic Information</Divider>
                <Form.Item label="Room Type Code" name="roomtype_code" rules={[{ required: true, message: 'Please enter room type code' }]}>
                    <Input placeholder="e.g., DLX, SUP" style={{ textTransform: 'uppercase' }} disabled={mode === 'edit'} />
                </Form.Item>

                {/* Enabled Toggle */}
                <Form.Item label="Room Status">
                    <Space align="center">
                        <Switch
                            checked={enabled}
                            onChange={setEnabled}
                            checkedChildren="Enabled"
                            unCheckedChildren="Disabled"
                        />
                        <span style={{ color: enabled ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>
                            {enabled ? 'This room is visible to guests' : 'This room is hidden from guests'}
                        </span>
                    </Space>
                </Form.Item>

                {/* Availability Settings */}
                <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Max Availability"
                            name="max_avail"
                            extra="Maximum rooms available per night. Leave blank for no limit."
                        >
                            <InputNumber
                                min={0}
                                precision={0}
                                style={{ width: '100%' }}
                                placeholder="e.g. 10"
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Availability Offset"
                            name="avail_offset"
                            extra="Number of rooms to hold back from availability (0 = no reduction)."
                        >
                            <InputNumber
                                min={0}
                                precision={0}
                                style={{ width: '100%' }}
                                placeholder="e.g. 0"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider></Divider>
                <Form.Item label="Cover Image">
                    <ImageUploader
                        existingUrl={coverImageState.existingUrl}
                        previewUrl={coverImageState.previewUrl}
                        onChange={(next) => setCoverImageState(prev => ({ ...prev, ...next }))}
                        onRemove={() => setCoverImageState({ file: null, previewUrl: undefined, existingUrl: undefined })}
                    />
                </Form.Item>

                {/* Fixed Gallery Images with scroll container */}
                <Form.Item label="Gallery Images">
                    <UploadGalleryStyle>
                        <Upload
                            listType="picture-card"
                            fileList={galleryImages}
                            onChange={({ fileList }) => setGalleryImages(fileList)}
                            onPreview={handlePreview}
                            beforeUpload={beforeUploadGallery}
                            multiple
                        >
                            {galleryImages.length >= MAX_ROOM_IMAGES ? null : uploadButton}
                        </Upload>
                    </UploadGalleryStyle>
                    {galleryImages.length >= MAX_ROOM_IMAGES ? (
                        <Alert
                            type="warning"
                            showIcon
                            title={`Maximum ${MAX_ROOM_IMAGES} images reached`}
                            description="Delete an image to upload a new one."
                            style={{ marginTop: 12 }}
                        />
                    ) : galleryImages.length > 0 && (
                        <div style={{ marginTop: 4, color: '#8c8c8c', fontSize: 12 }}>
                            {galleryImages.length} image{galleryImages.length !== 1 ? 's' : ''} uploaded
                        </div>
                    )}
                </Form.Item>

                <div style={{ display: "block" }}>
                    <Divider>Room Content (Multilingual)</Divider>
                </div>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    {activeLang !== 'en' && (
                        <Button onClick={handleCopyFromEn}>
                            Copy from EN
                        </Button>
                    )}
                    <Button icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)} disabled={availableLanguagesToAdd.length === 0}>
                        Add Language
                    </Button>
                </div>

                <Tabs activeKey={activeLang} onChange={setActiveLang}>
                    {editableLangs.map(lang => (
                        <TabPane tab={allSupportedLanguages[lang] || lang} key={lang} forceRender>
                            <Form.Item label={`Display Name`} name={['contents', lang, 'display_name']} rules={[{ required: true, message: 'Please enter display name' }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name={['contents', lang, 'description']}
                                label={`Description`}
                                rules={[{ required: true, message: 'Please enter description' }]}
                                valuePropName="value"
                                trigger="onChange"
                            >
                                <TiptapEditor minHeight={160} disabled={false} />
                            </Form.Item>
                            <Form.Item
                                label="Highlights"
                                name={['contents', lang, 'highlights']}
                                extra="Use commas to separate each item (e.g., Sea View, Jacuzzi)."
                            >
                                <TextArea placeholder="e.g., Sea View, Jacuzzi, Balcony" />
                            </Form.Item>
                            <Form.Item
                                label="Facilities"
                                name={['contents', lang, 'amenities']}
                                extra="Use commas to separate each item (e.g., Wifi, TV, Air-con)."
                            >
                                <TextArea placeholder="e.g., Wifi, TV, Air-con, Mini-bar" />
                            </Form.Item>
                        </TabPane>
                    ))}
                </Tabs>

                <Divider />
                <Form.Item>
                    <Space size="middle">
                        <Button type="primary" htmlType="submit" loading={submitting || loading} icon={<SaveOutlined />} size="large">
                            {mode === 'create' ? 'Create Room' : 'Update Room'}
                        </Button>
                        {mode === 'edit' && onCancel && <Button onClick={onCancel} icon={<CloseOutlined />} size="large">Cancel</Button>}
                    </Space>
                </Form.Item>
            </Form>

            <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={handleCancel}>
                <img alt="example" style={{ width: '100%' }} src={previewImage} />
            </Modal>

            <Modal title="Add Language" open={isModalVisible} onOk={handleAddLanguage} onCancel={() => setIsModalVisible(false)} okButtonProps={{ disabled: !langToAdd }}>
                <Select placeholder="Select a language"
                    style={{ width: '100%' }}
                    onChange={setLangToAdd}
                    value={langToAdd}
                    options={availableLanguagesToAdd.map((l) => {
                        return {
                            label: allSupportedLanguages[l],
                            value: l,
                        }
                    })}
                >
                </Select>
            </Modal>
        </>
    );
};

export default RoomForm;
